// src/pages/ImportBooksPage.tsx
// インポート画面。外部の読書管理サービスからのデータ取り込みをまとめる。
// 現状対応: ブクログ / readee のエクスポートCSV取り込み。
// 今後、他サービスからのインポートもこのページに並列で追加していく想定。

import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAppTokens } from '../context/ThemeModeContext';
import { importBooklogCsv, importBooklogResolve, importReadeeCsv, importReadeeResolve } from '../utils/importApi';
import ImportSourceCard from '../components/ImportSourceCard';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

const ImportBooksPage: React.FC = () => {
  const TOKENS = useAppTokens();
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();

  // 未ログインならトップへ自動遷移。Cognitoセッション確認の非同期処理を待つため少し遅らせる。
  useEffect(() => {
    if (isLoggedIn) return;
    const t = window.setTimeout(() => {
      navigate('/', { replace: true });
    }, 1200);
    return () => window.clearTimeout(t);
  }, [isLoggedIn, navigate]);

  return (
    <Box sx={{ mt: 7, minHeight: 'calc(100vh - 56px)', background: TOKENS.bg, pb: 6 }}>
      <Box sx={{ maxWidth: 720, margin: 'auto', px: 2, py: { xs: 3, md: 4 } }}>
        <Button
          component={Link}
          to="/bibliography"
          sx={{ mb: 1.5, textTransform: 'none', color: TOKENS.textSub, pl: 0 }}
        >
          ← 書誌管理
        </Button>

        <Typography sx={{ fontSize: 22, fontWeight: 700, color: TOKENS.text, mb: 0.5 }}>インポート</Typography>
        <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub, mb: 3 }}>
          外部サービスから登録済みの本のデータをまとめて取り込みます
        </Typography>

        <Typography sx={{ fontSize: 13, fontWeight: 700, color: TOKENS.textSub, mb: 1.5 }}>
          外部サービスからのインポート
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <ImportSourceCard
            title="ブクログからデータをインポートする"
            description="「ブクログ」には登録した本をCSVファイルとして書き出せるエクスポート機能があります。このインポート機能では、ブクログがエクスポートするCSVファイルを読み込み、ReadLinkerの機能に対応する形でデータを取り込みます。"
            supportedItems={[
              '13桁ISBN → ReadLinkerの書誌データベースと照合し、本棚に登録します',
              'カテゴリ → 本棚名として取り込みます（同名の本棚が既にあれば統合します）',
              '読書メモ・レビュー → 抜粋・メモ（非公開）として取り込みます',
              '登録日時 → そのまま登録日時として引き継ぎます',
            ]}
            unsupportedNote="評価・読書状況・タグ・読了日・タイプ・ページ数は、ReadLinkerに対応する機能がないため取り込まれません。また、既に登録済みの本（ISBNが一致）は重複登録を避けるためスキップされます。"
            importFn={importBooklogCsv}
            resolveFn={importBooklogResolve}
          />

          <ImportSourceCard
            title="readeeからデータをインポートする"
            description="「readee」には登録した本をCSVファイルとして書き出せるエクスポート機能があります。このインポート機能では、readeeがエクスポートするCSVファイルを読み込み、ReadLinkerの機能に対応する形でデータを取り込みます。インポートファイルにはreadee_all.csvを使用してください。"
            supportedItems={[
              'ISBN → ReadLinkerの書誌データベースと照合し、本棚に登録します',
              '本棚 → 本棚名として取り込みます（同名の本棚が既にあれば統合します）',
              'メモ・レビュー（レビュー見出しを含む） → 抜粋・メモ（非公開）として取り込みます',
              '登録日時 → そのまま登録日時として引き継ぎます',
            ]}
            unsupportedNote="評価・読書状況・タグ・購入日・読了日・読書開始日は、ReadLinkerに対応する機能がないため取り込まれません。また、既に登録済みの本（ISBNが一致）は重複登録を避けるためスキップされます。"
            importFn={importReadeeCsv}
            resolveFn={importReadeeResolve}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ImportBooksPage;
